import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import type { ScrapingJob } from '@/features/Scraping/api/dto';
import { scrapingService } from '@/features/Scraping/api/service';
import {
  scraperConfigMap,
  type ScraperConfigs,
} from '@/features/Scraping/configs';
import {
  SCRAPER_CATEGORY,
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
} from '@/features/Scraping/types';

const SCRAPING_JOBS_QUERY_KEY = 'dashboard-template-scraping-jobs';
const PAGE_SIZE = 100;

/**
 * COMMERCE 카테고리에 해당하는 스크래퍼인지 확인
 */
const isCommerceScraperJob = (job: ScrapingJob): boolean => {
  const config: ScraperConfigs | undefined = scraperConfigMap.get(
    job.scraper_name,
  );
  return config?.category === SCRAPER_CATEGORY.COMMERCE;
};

type UseScrapingJobsQueryOptions = {
  onlyMyJobs?: boolean;
  currentUserId?: string;
  searchTerm?: string;
};

/**
 * 대시보드 템플릿용 스크래핑 job 조회 훅
 *
 * - ONETIME 타입만 조회
 * - 클라이언트 사이드 COMMERCE 카테고리 필터링
 * - useInfiniteQuery로 "더보기" 데이터 누적
 * - "내 job만 보기" 필터
 * - searchTerm 검색 필터 (title, product_name)
 * - COMMERCE 필터 후 결과가 비어있으면 자동으로 추가 페이지 로드
 */
export const useScrapingJobsQuery = (options?: UseScrapingJobsQueryOptions) => {
  const query = useInfiniteQuery({
    queryKey: [SCRAPING_JOBS_QUERY_KEY],
    queryFn: ({ pageParam }) =>
      scrapingService.retrieveScrapingJobs({
        type: SCRAPING_JOB_TYPE.ONETIME,
        status: [SCRAPING_JOB_STATUS.COMPLETED],
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      // 실제 반환된 아이템 수 기반으로 다음 offset 계산
      const nextOffset = lastPageParam + lastPage.jobs.length;
      return nextOffset < lastPage.total_count ? nextOffset : undefined;
    },
  });

  // 모든 페이지 데이터를 평탄화
  const allJobs = useMemo(
    () => query.data?.pages.flatMap((page) => page.jobs) ?? [],
    [query.data?.pages],
  );

  // COMMERCE 카테고리 + "내 job만 보기" 필터 적용
  const filteredJobs = useMemo(() => {
    let jobs = allJobs.filter(isCommerceScraperJob);

    if (options?.onlyMyJobs && options.currentUserId) {
      jobs = jobs.filter(
        (job) => job.updated_by === options.currentUserId,
      );
    }

    return jobs;
  }, [allJobs, options?.onlyMyJobs, options?.currentUserId]);

  // 검색 필터링 (title, product_name 매칭)
  const searchFilteredJobs = useMemo(() => {
    const term = options?.searchTerm?.toLowerCase();
    if (!term) return filteredJobs;
    return filteredJobs.filter(
      (job) =>
        job.title?.toLowerCase().includes(term) ||
        job.scraper_input?.product_name?.toLowerCase().includes(term),
    );
  }, [filteredJobs, options?.searchTerm]);

  // COMMERCE 필터 후 결과가 비어있으면 자동으로 추가 페이지 로드
  useEffect(() => {
    if (query.isLoading || query.isError || query.isFetchingNextPage) return;
    if (!query.hasNextPage) return;
    if (searchFilteredJobs.length > 0) return;
    query.fetchNextPage();
  }, [
    searchFilteredJobs.length,
    query.hasNextPage,
    query.isError,
    query.isFetchingNextPage,
    query.isLoading,
    query.fetchNextPage,
  ]);

  const totalCount = query.data?.pages[0]?.total_count ?? 0;

  const loadMore = useCallback(() => {
    if (query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query.hasNextPage, query.fetchNextPage]);

  return {
    /** 검색 필터 적용된 jobs (테이블 표시용) */
    jobs: searchFilteredJobs,
    /** COMMERCE+myJobs 필터만 적용된 전체 jobs (선택 상태 파생용) */
    allJobs: filteredJobs,
    /** 전체 jobs 수 (헤더 카운트 표시용) */
    allJobsCount: filteredJobs.length,
    totalCount,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasMore: query.hasNextPage,
    loadMore,
    isFetchingMore: query.isFetchingNextPage,
  };
};
